import { SECTIONS } from "@/lib/sections";
import { pieceColorVar } from "@/game/pieces";
import { Tetromino } from "./Tetromino";

interface NextQueueProps {
  readonly activeId: string;
}

/**
 * THE NEXT QUEUE — the signature of the site.
 *
 * There are seven sections and seven tetrominoes, so the page index is drawn as
 * a Tetris piece queue. The section you are reading is the piece "in play":
 * solid, indented, and named. Everything ahead of you is still in the bag,
 * drawn hollow. Scroll progress is therefore literally a line counter.
 *
 * On wide screens it is a fixed rail down the left edge; on narrow ones it
 * folds into a dock along the bottom.
 */
export function NextQueue({ activeId }: NextQueueProps) {
  const activeIndex = Math.max(
    0,
    SECTIONS.findIndex((section) => section.id === activeId),
  );

  return (
    <nav aria-label="Sections" className="pointer-events-none">
      {/* ── Wide screens: the rail ───────────────────────────────────── */}
      <div
        className="pointer-events-auto fixed left-0 top-0 z-40 hidden h-screen w-[var(--rail-width)]
          flex-col items-center justify-center gap-1 border-r-2 border-ink bg-field-deep
          xl:flex"
      >
        <span className="meta absolute top-5 text-[0.5625rem] opacity-70">Next</span>

        {SECTIONS.map((section, index) => {
          const isActive = index === activeIndex;
          const isCleared = index < activeIndex;

          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              aria-current={isActive ? "true" : undefined}
              title={section.label}
              className={`group relative flex h-[54px] w-full items-center justify-center
                transition-[background-color] duration-100
                ${isActive ? "bg-ink" : "hover:bg-field"}`}
            >
              <span
                className={`absolute left-1 meta text-[0.5rem] leading-none
                  ${isActive ? "text-field" : "text-ink opacity-45"}`}
              >
                {section.index}
              </span>

              <Tetromino
                type={section.piece}
                cell={9}
                filled={isActive || isCleared}
                className={isActive ? "" : "opacity-80"}
              />

              {/* The name only surfaces for the piece in play. */}
              <span
                className={`meta absolute bottom-1 text-[0.5rem] leading-none text-field
                  ${isActive ? "opacity-100" : "opacity-0"}`}
              >
                {section.short}
              </span>
            </a>
          );
        })}

        {/* Progress, counted the only way this club counts anything. */}
        <div className="absolute bottom-5 flex flex-col items-center gap-1">
          <span className="meta text-[0.5rem] opacity-70">Lines</span>
          <span className="font-mono text-lg font-medium leading-none tabular-nums">
            {String(activeIndex).padStart(2, "0")}
          </span>
        </div>
      </div>

      {/* ── Narrow screens: the dock ─────────────────────────────────── */}
      <div
        className="pointer-events-auto fixed bottom-0 left-0 z-40 flex w-full items-stretch
          border-t-2 border-ink bg-field-deep xl:hidden"
      >
        {SECTIONS.map((section, index) => {
          const isActive = index === activeIndex;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              aria-current={isActive ? "true" : undefined}
              className={`flex flex-1 flex-col items-center justify-center gap-1 py-2
                ${isActive ? "bg-ink" : ""}`}
            >
              <Tetromino
                type={section.piece}
                cell={6}
                filled={index <= activeIndex}
              />
              <span
                className="h-[3px] w-5"
                style={{ background: isActive ? pieceColorVar(section.piece) : "transparent" }}
              />
              <span className="sr-only">{section.label}</span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
